import { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TableSortLabel
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Info from './Info';
import LanguageSelector from './LanguageSelector';
import { useAdminLanguage } from './AdminI18n';
import useTableSort from './useTableSort';
function GarageAdmin() {
  const { t } = useAdminLanguage();
  const [garages, setGarages] = useState([]);
  const [formData, setFormData] = useState({ nom: '', adresse: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [language, setLanguage] = useState('fr');

  useEffect(() => {
    fetchGarages();
  }, [language]);

  const fetchGarages = async () => {
    try {
      const response = await fetch(`/api/garages?lang=${language}`);
      const data = await response.json();
      setGarages(data);
      setLoading(false);
    } catch (err) {
      setError('Erreur lors du chargement des dépanneurs');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.nom.trim() || !formData.adresse.trim()) {
        setError('Tous les champs sont obligatoires');
        return;
      }

      setLoading(true);
      setError('');
      setSuccess('');

      if (isEditing) {
        await fetch(`/api/garages/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...formData, lang: language }),
        });
        setSuccess('Dépanneur mis à jour avec succès');
      } else {
        await fetch('/api/garages', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...formData, lang: language }),
        });
        setSuccess('Dépanneur ajouté avec succès');
      }

      setFormData({ nom: '', adresse: '' });
      setIsEditing(false);
      setEditingId(null);
      fetchGarages();
    } catch (err) {
      setError('Erreur lors de l\'enregistrement du dépanneur');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (garage) => {
    setIsEditing(true);
    setEditingId(garage.id);
    setFormData({ nom: garage.nom, adresse: garage.adresse });
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await fetch(`/api/garages/${id}?lang=${language}`, {
        method: 'DELETE',
      });
      setSuccess('Dépanneur supprimé avec succès');
      fetchGarages();
    } catch (err) {
      setError('Erreur lors de la suppression du dépanneur');
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => ([
    { id: 'nom', label: t('Nom'), sortable: true },
    { id: 'adresse', label: t('Adresse'), sortable: true },
  ]), [t]);

  const { order, orderBy, sortedRows, handleRequestSort } = useTableSort(garages, {
    defaultOrderBy: 'nom',
  });

  const renderCellContent = (garage, columnId) => garage[columnId];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Gestion des dépanneurs')}
          </Typography>

          <Info type="garage" />

          <LanguageSelector 
            language={language} 
            onLanguageChange={setLanguage} 
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t(error)}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t(success)}
            </Alert>
          )}

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {t(isEditing ? 'Modifier le dépanneur' : 'Ajouter un dépanneur')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="nom"
                  label={t('Nom')}
                  autoComplete="off"
                  value={formData.nom}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="adresse"
                  label={t('Adresse')}
                  autoComplete="off"
                  value={formData.adresse}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={20} />}
                >
                  {loading ? t('Enregistrement...') : t(isEditing ? 'Modifier' : 'Ajouter')}
                </Button>
                {isEditing && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setIsEditing(false);
                      setEditingId(null);
                      setFormData({ nom: '', adresse: '' });
                    }}
                    sx={{ ml: 2 }}
                  >
                    {t('Annuler')}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              {t('Liste des dépanneurs')}
            </Typography>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        sortDirection={orderBy === column.id ? order : false}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : 'asc'}
                            onClick={() => handleRequestSort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                    <TableCell>{t('Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRows.map((garage) => (
                    <TableRow key={garage.id}>
                      {columns.map((column) => (
                        <TableCell key={`${garage.id}-${column.id}`}>
                          {renderCellContent(garage, column.id)}
                        </TableCell>
                      ))}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(garage)}
                            color="primary"
                            disabled={loading}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(garage.id)}
                            color="error"
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Fade>
    </Container>
  );
}

export default GarageAdmin;